// src/components/ReadinessBanner.jsx
// P0.21 task 21.10f — the GLOBAL readiness banner.
//
// WHY THIS EXISTS RATHER THAN THE BATCH REPORTING 21.10f ORIGINALLY PROPOSED
// The task was stuck on a three-way choice about the weekly owner email: report
// only critical gaps and stay silent about the rest, report everything and make
// the mail noise for four of six live tenants, or split the two. The owner
// proposed a fourth option that dissolves it — a persistent in-app message with
// a COUNT and a BUTTON.
//
// An email can be filtered, and once it is, the CRITICAL notice is filtered with
// it. A banner cannot be filtered, it appears exactly when someone is using the
// product, and the button removes the "where do I even do that" step that a list
// of field names leaves behind.
//
// WHAT IS NEW HERE versus the banner already in Settings
// ProfileSettingsPage has rendered a readiness list since P0.20. It is not
// global (it only shows inside Settings, where the user already is), it has no
// count, and it has no button. Those three are the work.
//
// THE BANNER SHOWS ONLY WHEN SOMETHING CRITICAL IS MISSING.
//
// 21.10f originally gated this on `missing` — anything at all — on the
// reasoning that a passive, dismissible banner was the one surface where a
// non-critical gap could be reported without becoming noise. That held when the
// only non-critical fields were two text boxes a user could fill in.
//
// It stopped holding on 11 Sep 2026 when four DERIVED sets joined the list.
// They cannot be filled in directly, two of them resolve on a plain Save, and
// one — the messaging libraries — may legitimately NEVER derive for a services
// business whose prose describes what it sells rather than what goes wrong for
// its buyers. For that tenant the old gate put a banner on every page, forever,
// with no action that could ever clear it. That is precisely the "setting
// nobody remembers turning on" this component's own dismiss comment warns
// against.
//
// So the gate is now `criticalMissing`, which is what "before this tool can
// work at full power" was always describing — and it matches the cron path
// (tenant-loop.js) and the owner email (notifyAdmins.js), which were already
// critical-only. The Settings page still lists EVERY gap, because that is the
// one place a user can act on one.
import React, { useState } from 'react';
import './ReadinessBanner.css';

/**
 * Order the fields so "start with" names something worth starting with.
 *
 * Critical first, and within critical the field whose absence costs most:
 * company_vertical before company_name, because an unnamed company degrades to
 * "Your Company" — visibly wrong, and the user already knows their own name —
 * while a missing vertical silently removes all industry framing from every
 * prompt with nothing on screen to show for it.
 */
const WEIGHT = {
  company_vertical:   0,
  company_name:       1,
  commercial_profile: 2,
  company_services:   3,
  company_descriptor: 4,
  account_attributes: 5,
  // P0.21 task 21.6d. After the attribute set: both are derived from the prose
  // fields above, and both are non-critical, so they belong at the end where
  // "start with" never names one of them while a prose field is still empty.
  derived_practice_areas: 6,
  // Last: the most downstream of the three derived sets, and the one whose
  // absence degrades rather than breaks — a parser vocabulary still scores, just
  // without trade sources or buyer roles.
  derived_vocabulary: 7,
  // P0.21 task 21.8. Last of all: derived from the same prose as the three
  // above, non-critical, and its consequence is felt in outbound drafts rather
  // than in stored data.
  derived_messaging_libraries: 8,
};

export function orderForGuidance(missing) {
  return [...(missing || [])].sort((a, b) => {
    if (a.critical !== b.critical) return a.critical ? -1 : 1;
    const wa = WEIGHT[a.key] ?? 99;
    const wb = WEIGHT[b.key] ?? 99;
    if (wa !== wb) return wa - wb;
    return String(a.key).localeCompare(String(b.key));
  });
}

export default function ReadinessBanner({ readiness, onNavigate }) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;
  if (!readiness) return null;

  // Critical only — see the header. A tenant with every critical field set but
  // a derived set outstanding is not "not at full power"; they are configured,
  // and Settings is where the remaining improvements are listed.
  const ordered = orderForGuidance(readiness.criticalMissing);
  if (!ordered.length) return null;

  const count = ordered.length;
  const first = ordered[0];
  const anyCritical = true;

  // Every readiness field lives in the Company section, so there is one
  // destination rather than per-field routing. The hash is what
  // ProfileSettingsPage reads to pick its tab; setting it BEFORE navigating
  // means the page mounts on the right section instead of mounting on Identity
  // and jumping.
  // THE BUTTON DID NOTHING WHEN YOU WERE ALREADY ON THE PROFILE PAGE, and the
  // banner renders on every view, so that was the one place it was guaranteed to
  // be visible and inert. Three independent reasons, any one sufficient:
  //
  //   1. Setting window.location.hash to the value it ALREADY holds fires no
  //      hashchange event.
  //   2. Nothing listens for hashchange anyway — Dashboard.jsx and
  //      ProfileSettingsPage.jsx both read the hash in a useState(() => …)
  //      INITIALISER, which runs once at mount.
  //   3. onNavigate('profile') calls setCurrentView('profile'), and React bails
  //      out of a re-render when the state is unchanged.
  //
  // So the page never remounts and its section initialiser never re-runs.
  //
  // Fixed by dispatching the event rather than by making section selection
  // reactive. Those initialisers exist to stop an OAuth return rendering the
  // dashboard for a frame and then jumping to #profile/Integrations; converting
  // them to effects would reintroduce that flash to fix a button.
  const go = () => {
    const target = `profile/${first.section || 'Company'}`;
    try {
      const already = (window.location.hash || '').replace(/^#/, '') === target;
      window.location.hash = target;
      // Same value, so no hashchange fired. Say so explicitly.
      if (already) window.dispatchEvent(new HashChangeEvent('hashchange'));
    } catch { /* ignore */ }
    if (typeof onNavigate === 'function') onNavigate('profile');
  };

  return (
    <div className={`dm-readiness${anyCritical ? ' dm-readiness--critical' : ''}`} role="status">
      <div className="dm-readiness-text">
        {/* "KEY SETTINGS" ONLY WHEN SOMETHING IS ACTUALLY CRITICAL.
            Every missing field is still counted — that decision is from 21.10f
            and stands — but calling three OPTIONAL derived sets "key settings to
            complete" overstates them, and it read as self-contradicting beside
            an action line saying "Nothing to fill in directly". */}
        <strong>
          {anyCritical
            ? (count === 1
              ? 'There is 1 key setting to complete'
              : `There are ${count} key settings to complete`)
            : (count === 1
              ? 'There is 1 thing your AI could know about you'
              : `There are ${count} things your AI could know about you`)}
        </strong>{' '}
        {anyCritical
          ? 'before this tool can work at full power.'
          : ''}{' '}
        {/* P0.21 task 21.6d — what to do, not only what is wrong. The button
            below says "Start with {label}", and for a DERIVED entry that label
            names a panel rather than a field, so the action line is what stops
            it sending someone to look for a box that does not exist. */}
        <span className="dm-readiness-hint">{first.action || first.impact}</span>
      </div>
      <div className="dm-readiness-actions">
        {/* "Start with {label}" named a PANEL for the three derived entries —
            "Start with What to research about an account" is not something
            anyone can start with, and it sat next to an action line saying there
            was nothing to fill in. A derived entry is reached by opening the
            section and saving, so the button says that instead. */}
        <button type="button" className="dm-readiness-btn" onClick={go}>
          {first.derived ? 'Open Settings' : `Start with ${first.label}`}
        </button>
        {/* Session-only, deliberately: nothing is persisted, so it returns on
            the next load. A permanent dismissal on a gap that degrades every
            prompt would be a setting nobody remembers turning on. */}
        <button
          type="button"
          className="dm-readiness-dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss until next reload"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
